import React, { FC, useState, useContext } from 'react';
import { Button, Modal, Tooltip } from '@clearcover/atlas';
import classnames from 'classnames';

import styles from './styles.module.css';
import { useLazyApi } from 'Shared/services/Api';
import { useParams, useHistory } from 'react-router-dom';
import { AxiosError } from 'axios';
import { draftEndpointModifier, PolicyContext } from 'Policies/utils';
import {
  FormModalErrorMessage,
  FormModalUnderWritingErrors,
} from 'Shared/components';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import { VehicleForm } from '../VehicleForm';
import {
  DriverType,
  VehicleType,
  AddressType,
} from '@clearcover/core-app-types';
import { combineStrings } from 'Shared/utils';
import { useModalHashChange } from 'Shared/hooks';

import {
  editVehiclePermissionsPolicy,
  removeVehiclePermissionsPolicy,
} from 'Admin/authPolicies';
import { ConflictingDraftModal } from 'Policies/components/ConflictingDraftModal';

export const EditVehicleButtons: FC<{
  vehicle: VehicleType;
  drivers: Array<DriverType>;
  mailingAddress: AddressType;
  withBackground?: boolean;
  disableRemoveButton?: boolean;
  removeButtonTooltip?: string;
  isNSA: boolean;
}> = ({
  vehicle,
  drivers,
  mailingAddress,
  withBackground = true,
  disableRemoveButton = false,
  removeButtonTooltip = '',
  isNSA,
}) => {
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();
  const {
    refetchHandler,
    details: { draftType },
    flags: { renewalQuotesEnabled },
    resourceEffectiveGrants,
  } = useContext(PolicyContext);
  const history = useHistory();
  const { make, model, year, identifier: vehicleId } = vehicle;
  const { underwritingErrors, setUnderwritingErrors } = useUnderwritingErrors();
  const [error, setError] = useState<AxiosError | undefined>();
  const [{ loading }, removeVehicle] = useLazyApi<{
    draftId: string;
  }>({
    url: `/policies/${policyId}/vehicles/${vehicleId}/remove${draftEndpointModifier(
      draftType,
      renewalQuotesEnabled
    )}`,
  });

  const { modalHashChange } = useModalHashChange('editVehicle');

  const canRemoveVehicle = removeVehiclePermissionsPolicy(
    resourceEffectiveGrants
  );

  const canEditVehicle = editVehiclePermissionsPolicy(resourceEffectiveGrants);

  const handleVehicleRemove = async (options?: {
    bypassUnderwriting?: boolean;
  }) => {
    const execConfig = options?.bypassUnderwriting
      ? { params: { bypassUnderwriting: true } }
      : undefined;
    return removeVehicle(execConfig)
      .then(draft => {
        draftId === draft.data.draftId
          ? refetchHandler()
          : history.push(`/policies/${policyId}/draft/${draft.data.draftId}`);
      })
      .catch(err => {
        setError(err);
        setUnderwritingErrors(err);
      });
  };

  return (
    <div
      data-testid={`${vehicleId}-edit-vehicle-buttons`}
      className={classnames(styles.container, {
        [styles.background]: withBackground,
      })}
    >
      {canRemoveVehicle && (
        <Modal
          toggle={openRemoveVehicleModal => (
            <ConflictingDraftModal
              onApprove={openRemoveVehicleModal}
              renderToggle={openModal => (
                <Tooltip tooltip={removeButtonTooltip} position="top">
                  <Button
                    className={styles.editVehicleButtons}
                    data-testid={`${vehicleId}-remove-vehicle-button`}
                    variant={'danger-secondary'}
                    onClick={() => {
                      openModal();
                      setError(undefined);
                      setUnderwritingErrors(undefined);
                    }}
                    disabled={disableRemoveButton}
                  >
                    Remove
                  </Button>
                </Tooltip>
              )}
            />
          )}
        >
          {close => (
            <div className={styles.modal}>
              <div data-testid={`${vehicleId}-remove-vehicle-modal`}>
                <span>
                  Are you sure you want to remove{' '}
                  {combineStrings([year, make, model])}?
                </span>
                <div className={styles.buttonGroup}>
                  <Button
                    disabled={loading}
                    data-testid={`${vehicleId}-remove-vehicle-cancel`}
                    block
                    onClick={close}
                    variant="danger-secondary"
                  >
                    Cancel Removal
                  </Button>
                  <Button
                    onClick={() => handleVehicleRemove()}
                    isSubmitting={loading}
                    data-testid={`${vehicleId}-remove-vehicle-confirm`}
                    block
                    variant="danger"
                  >
                    Yes, Revise Estimate
                  </Button>
                </div>
              </div>
              <div className={styles.errorContainer}>
                {underwritingErrors.length === 0 && (
                  <FormModalErrorMessage>
                    {error?.message}
                  </FormModalErrorMessage>
                )}
                <FormModalUnderWritingErrors
                  grants={resourceEffectiveGrants}
                  errors={underwritingErrors}
                  submitIgnoringUnderwriting={() =>
                    handleVehicleRemove({ bypassUnderwriting: true })
                  }
                />
              </div>
            </div>
          )}
        </Modal>
      )}

      {canEditVehicle && (
        <Modal
          onVisibilityChange={modalHashChange}
          variant={'full'}
          toggle={openEditVehicleModal => (
            <ConflictingDraftModal
              onApprove={openEditVehicleModal}
              renderToggle={openModal => (
                <Button
                  className={styles.editVehicleButtons}
                  size="sm"
                  onClick={openModal}
                  variant="secondary"
                  data-testid={`${vehicleId}-edit-vehicle-button`}
                >
                  Edit
                </Button>
              )}
            />
          )}
        >
          {close => (
            <VehicleForm
              type="update"
              vehicle={vehicle}
              drivers={drivers}
              close={close}
              mailingAddress={mailingAddress}
              isNSA={isNSA}
            />
          )}
        </Modal>
      )}
    </div>
  );
};
